import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useBranding } from "../lib/branding";
import { Plus, Save, Trash2, AlertTriangle, Upload, RefreshCw, Shield, X, FileText } from "lucide-react";

const PAY_BASIS_OPTIONS = ["PAYE", "CIS-subbie"];

const TABS = [
  { id: "price", label: "Price schedule" },
  { id: "types", label: "Structure types" },
  { id: "areas", label: "Areas & zones" },
  { id: "ops", label: "Operatives" },
  { id: "mccain", label: "McCain contacts" },
  { id: "brand", label: "Branding" },
];

export default function Admin() {
  const [tab, setTab] = useState("price");

  return (
    <div data-testid="admin-page" style={{ padding: "24px 24px 64px" }}>
      <header style={{ marginBottom: 18 }}>
        <div className="mono" style={{ color: "#ff5a00", fontWeight: 600, fontSize: 12, letterSpacing: "0.1em" }}>ADMIN · CONFIGURATION</div>
        <h1 style={{ fontSize: 28, fontWeight: 900, margin: "4px 0 0" }}>Light admin CRUD</h1>
        <div style={{ color: "#64748b", marginTop: 4 }}>Never be blocked by a missing rate, structure type, area, or operative. Pay fields are record-only — no calculations live here.</div>
      </header>

      <div style={{ display: "flex", borderBottom: "1px solid #e2e8f0", marginBottom: 16, gap: 4 }} data-testid="admin-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className="btn-ghost"
            style={{
              border: "none",
              borderRadius: 0,
              borderBottom: tab === t.id ? "3px solid #ff5a00" : "3px solid transparent",
              padding: "10px 14px",
              fontWeight: 700,
              color: tab === t.id ? "#0f172a" : "#64748b",
              background: "transparent",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "price" ? <PriceSchedule /> : null}
      {tab === "types" ? <StructureTypes /> : null}
      {tab === "areas" ? <Areas /> : null}
      {tab === "ops" ? <Operatives /> : null}
      {tab === "mccain" ? <McCainContacts /> : null}
      {tab === "brand" ? <Branding /> : null}
    </div>
  );
}

// ---------------- Price schedule ----------------
function PriceSchedule() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ ref: "", description: "", unit: "Each", unit_price: 0, notes: "" });
  const [err, setErr] = useState("");

  const load = () => api.get("/price-schedule").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/admin/price-schedule", draft);
      setDraft({ ref: "", description: "", unit: "Each", unit_price: 0, notes: "" });
      load();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
  };

  const save = async (item) => {
    setErr("");
    try {
      await api.put(`/admin/price-schedule/${item.ref}`, item);
      load();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
  };

  const del = async (ref) => {
    if (!window.confirm(`Delete rate ${ref}?`)) return;
    setErr("");
    try {
      await api.delete(`/admin/price-schedule/${ref}`);
      load();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
  };

  return (
    <div>
      {err ? <ErrBox msg={err} /> : null}
      <div className="card" style={{ padding: 14, marginBottom: 14 }} data-testid="price-add-card">
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Add rate</div>
        <form onSubmit={add} style={{ display: "grid", gridTemplateColumns: "100px 1fr 110px 120px 1fr 110px", gap: 8 }}>
          <input data-testid="price-add-ref" className="input-base" placeholder="Ref (e.g. 1D)" value={draft.ref} onChange={(e) => setDraft({ ...draft, ref: e.target.value })} required />
          <input data-testid="price-add-desc" className="input-base" placeholder="Description" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} required />
          <select data-testid="price-add-unit" className="input-base" value={draft.unit} onChange={(e) => setDraft({ ...draft, unit: e.target.value })}>
            <option>Each</option><option>Metre</option><option>m²</option><option>Event</option><option>Day</option>
          </select>
          <input data-testid="price-add-price" className="input-base" type="number" step="0.01" min="0" placeholder="£" value={draft.unit_price} onChange={(e) => setDraft({ ...draft, unit_price: parseFloat(e.target.value || 0) })} />
          <input data-testid="price-add-notes" className="input-base" placeholder="Notes" value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
          <button data-testid="price-add-submit" className="btn-primary" type="submit"><Plus size={14} style={{ verticalAlign: -2 }} /> Add</button>
        </form>
      </div>

      <div className="card" style={{ padding: 0 }} data-testid="price-table">
        <table>
          <thead><tr><th>Ref</th><th>Description</th><th>Unit</th><th style={{ textAlign: "right" }}>Unit £</th><th>Notes</th><th></th></tr></thead>
          <tbody>
            {items.map((item) => <PriceRow key={item.ref} item={item} onSave={save} onDelete={del} />)}
            {items.length === 0 ? <tr><td colSpan={6} style={{ padding: 20, textAlign: "center", color: "#94a3b8" }}>No price rates</td></tr> : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PriceRow({ item, onSave, onDelete }) {
  const [v, setV] = useState(item);
  useEffect(() => setV(item), [item]);
  const dirty = JSON.stringify(v) !== JSON.stringify(item);
  return (
    <tr data-testid={`price-row-${item.ref}`}>
      <td className="mono" style={{ fontWeight: 700 }}>{item.ref}</td>
      <td><input className="input-base" value={v.description || ""} onChange={(e) => setV({ ...v, description: e.target.value })} /></td>
      <td>
        <select className="input-base" value={v.unit} onChange={(e) => setV({ ...v, unit: e.target.value })}>
          <option>Each</option><option>Metre</option><option>m²</option><option>Event</option><option>Day</option>
        </select>
      </td>
      <td><input className="input-base mono" style={{ textAlign: "right" }} type="number" step="0.01" value={v.unit_price} onChange={(e) => setV({ ...v, unit_price: parseFloat(e.target.value || 0) })} /></td>
      <td><input className="input-base" value={v.notes || ""} onChange={(e) => setV({ ...v, notes: e.target.value })} /></td>
      <td style={{ whiteSpace: "nowrap" }}>
        <button className="btn-primary" disabled={!dirty} style={{ padding: "4px 10px", marginRight: 4 }} onClick={() => onSave(v)} data-testid={`price-save-${item.ref}`}><Save size={12} style={{ verticalAlign: -2 }} /></button>
        <button className="btn-ghost" style={{ padding: "4px 10px" }} onClick={() => onDelete(item.ref)} data-testid={`price-del-${item.ref}`}><Trash2 size={12} style={{ verticalAlign: -2 }} /></button>
      </td>
    </tr>
  );
}

// ---------------- Structure types ----------------
function StructureTypes() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ name: "", description: "", default_duty_class: "General purpose (2.0 kN/m²)" });
  const [err, setErr] = useState("");
  const load = () => api.get("/structure-types").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault(); setErr("");
    try { await api.post("/admin/structure-types", draft); setDraft({ name: "", description: "", default_duty_class: "General purpose (2.0 kN/m²)" }); load(); }
    catch (e) { setErr(e.response?.data?.detail || e.message); }
  };
  const del = async (id) => { if (!window.confirm("Delete type?")) return; await api.delete(`/admin/structure-types/${id}`); load(); };

  return (
    <div>
      {err ? <ErrBox msg={err} /> : null}
      <div className="card" style={{ padding: 14, marginBottom: 14 }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Add structure type</div>
        <form onSubmit={add} style={{ display: "grid", gridTemplateColumns: "180px 1fr 220px 100px", gap: 8 }}>
          <input data-testid="type-add-name" className="input-base" placeholder="Name (e.g. Cantilever)" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} required />
          <input data-testid="type-add-desc" className="input-base" placeholder="Description" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          <select data-testid="type-add-duty" className="input-base" value={draft.default_duty_class} onChange={(e) => setDraft({ ...draft, default_duty_class: e.target.value })}>
            <option>Inspection (0.75 kN/m²)</option><option>Light (1.5 kN/m²)</option><option>General purpose (2.0 kN/m²)</option><option>Heavy (2.5 kN/m²)</option><option>Special (≥3.0 kN/m²)</option>
          </select>
          <button data-testid="type-add-submit" className="btn-primary" type="submit"><Plus size={14} style={{ verticalAlign: -2 }} /> Add</button>
        </form>
      </div>
      <div className="card" style={{ padding: 0 }} data-testid="types-table">
        <table>
          <thead><tr><th>Name</th><th>Description</th><th>Default duty</th><th></th></tr></thead>
          <tbody>
            {items.map((t) => (
              <tr key={t.id} data-testid={`type-row-${t.id}`}>
                <td style={{ fontWeight: 700 }}>{t.name}</td>
                <td>{t.description || "—"}</td>
                <td className="mono">{t.default_duty_class || "—"}</td>
                <td><button className="btn-ghost" style={{ padding: "4px 10px" }} onClick={() => del(t.id)} data-testid={`type-del-${t.id}`}><Trash2 size={12} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------- Areas ----------------
const HAZARDS = ["ammonia", "hot_oil", "dust_dsear", "steam", "confined_space", "flt_traffic", "cold"];
const ZONES = ["External", "Plant room", "Roof", "Low-care", "High-care", "Over open product"];

function Areas() {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");
  const [draft, setDraft] = useState({ name: "", description: "", zone_classification: "External", hazard_flags: [] });
  const load = () => api.get("/areas").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const toggle = (h) => setDraft((d) => ({ ...d, hazard_flags: d.hazard_flags.includes(h) ? d.hazard_flags.filter((x) => x !== h) : [...d.hazard_flags, h] }));
  const add = async (e) => {
    e.preventDefault(); setErr("");
    try { await api.post("/areas", draft); setDraft({ name: "", description: "", zone_classification: "External", hazard_flags: [] }); load(); }
    catch (e) { setErr(e.response?.data?.detail || e.message); }
  };
  const del = async (id) => { if (!window.confirm("Delete area?")) return; setErr(""); try { await api.delete(`/admin/areas/${id}`); load(); } catch (e) { setErr(e.response?.data?.detail || e.message); } };

  return (
    <div>
      {err ? <ErrBox msg={err} /> : null}
      <div className="card" style={{ padding: 14, marginBottom: 14 }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Add area</div>
        <form onSubmit={add} style={{ display: "grid", gap: 8 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 180px", gap: 8 }}>
            <input data-testid="area-add-name" className="input-base" placeholder="Area name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} required />
            <input data-testid="area-add-desc" className="input-base" placeholder="Description" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
            <select data-testid="area-add-zone" className="input-base" value={draft.zone_classification} onChange={(e) => setDraft({ ...draft, zone_classification: e.target.value })}>
              {ZONES.map((z) => <option key={z}>{z}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {HAZARDS.map((h) => (
              <button type="button" key={h} className={draft.hazard_flags.includes(h) ? "btn-primary" : "btn-ghost"} onClick={() => toggle(h)} style={{ padding: "4px 10px", fontSize: 12 }} data-testid={`area-haz-${h}`}>{h}</button>
            ))}
          </div>
          <div><button data-testid="area-add-submit" className="btn-primary" type="submit"><Plus size={14} style={{ verticalAlign: -2 }} /> Add area</button></div>
        </form>
      </div>
      <div className="card" style={{ padding: 0 }} data-testid="areas-table">
        <table>
          <thead><tr><th>Name</th><th>Zone</th><th>Hazards</th><th>Description</th><th></th></tr></thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} data-testid={`area-row-${a.id}`}>
                <td style={{ fontWeight: 700 }}>{a.name}</td>
                <td><span className="badge badge-slate">{a.zone_classification}</span></td>
                <td>{(a.hazard_flags || []).map((h) => <span key={h} className="badge badge-amber" style={{ marginRight: 4 }}>{h}</span>)}{!(a.hazard_flags || []).length ? "—" : null}</td>
                <td style={{ color: "#64748b" }}>{a.description || "—"}</td>
                <td><button className="btn-ghost" style={{ padding: "4px 10px" }} onClick={() => del(a.id)} data-testid={`area-del-${a.id}`}><Trash2 size={12} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------- Operatives ----------------
const OP_ROLES = ["Scaffolder", "Advanced", "Labourer", "Supervisor-SSSTS", "TWC"];
const OP_TYPES = ["Scaffolder", "Labourer"];
const SCAFFOLDER_GRADES = ["Part 1", "Part 2", "Advanced"];

function Operatives() {
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [err, setErr] = useState("");
  const [piiOp, setPiiOp] = useState(null);
  const blank = { full_name: "", role: "Scaffolder", op_type: "Scaffolder", scaffolder_grade: "Part 1", rams_signoff_authority: false, competence_basis: "", cisrs_number: "", cisrs_expiry: "", pay_basis: "PAYE", hourly_rate: 0, gang_id: "", status: "active", contact_number: "" };
  const [draft, setDraft] = useState(blank);
  const load = () => api.get("/operatives").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/subcontractor-companies").then((r) => setCompanies(r.data)).catch(() => setCompanies([]));
  }, []);

  const add = async (e) => {
    e.preventDefault(); setErr("");
    try { await api.post("/admin/operatives", draft); setDraft(blank); load(); }
    catch (e) { setErr(e.response?.data?.detail || e.message); }
  };
  const save = async (op) => { setErr(""); try { await api.put(`/admin/operatives/${op.id}`, op); load(); } catch (e) { setErr(e.response?.data?.detail || e.message); } };
  const del = async (id) => { if (!window.confirm("Delete operative?")) return; await api.delete(`/admin/operatives/${id}`); load(); };

  return (
    <div>
      {err ? <ErrBox msg={err} /> : null}
      <div className="card" style={{ padding: 14, marginBottom: 14 }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Add operative</div>
        <form onSubmit={add} style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 130px 130px 100px 100px 60px", gap: 8 }}>
          <input data-testid="op-add-name" className="input-base" placeholder="Full name" value={draft.full_name} onChange={(e) => setDraft({ ...draft, full_name: e.target.value })} required />
          <select data-testid="op-add-type" className="input-base" value={draft.op_type} onChange={(e) => setDraft({ ...draft, op_type: e.target.value, scaffolder_grade: e.target.value === "Labourer" ? null : (draft.scaffolder_grade || "Part 1") })}>{OP_TYPES.map((r) => <option key={r}>{r}</option>)}</select>
          {draft.op_type === "Scaffolder" ? (
            <select data-testid="op-add-grade" className="input-base" value={draft.scaffolder_grade || "Part 1"} onChange={(e) => setDraft({ ...draft, scaffolder_grade: e.target.value })}>{SCAFFOLDER_GRADES.map((g) => <option key={g}>{g}</option>)}</select>
          ) : <input className="input-base" value="—" disabled />}
          <input data-testid="op-add-cisrs" className="input-base" placeholder="CISRS #" value={draft.cisrs_number} onChange={(e) => setDraft({ ...draft, cisrs_number: e.target.value })} />
          <input data-testid="op-add-expiry" className="input-base" type="date" value={draft.cisrs_expiry || ""} onChange={(e) => setDraft({ ...draft, cisrs_expiry: e.target.value })} />
          <select data-testid="op-add-pay" className="input-base" value={draft.pay_basis} onChange={(e) => setDraft({ ...draft, pay_basis: e.target.value })}>{PAY_BASIS_OPTIONS.map((p) => <option key={p}>{p}</option>)}</select>
          <input data-testid="op-add-rate" className="input-base" type="number" step="0.01" placeholder="£/hr" value={draft.hourly_rate} onChange={(e) => setDraft({ ...draft, hourly_rate: parseFloat(e.target.value || 0) })} />
          <button data-testid="op-add-submit" className="btn-primary" type="submit"><Plus size={14} /></button>
        </form>
        <div className="mono" style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>
          Pay basis + hourly rate are <b>record-only</b> (no calc; payroll lives in Xero, Phase 2). The <b>RAMS sign-off authority</b> tickbox is set per row below — it is <b>orthogonal to grade</b>: a Part 1 scaffolder, an Advanced scaffolder, or a supervisor can each be ticked or not. The box decides, not the grade.
        </div>
      </div>

      <div className="card" style={{ padding: 0 }} data-testid="ops-table">
        <table>
          <thead><tr><th>Name</th><th>Type / Grade</th><th>CISRS</th><th>Expiry</th><th>Pay</th><th>£/hr</th><th>Gang</th><th>RAMS sign-off auth.</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {items.map((op) => <OpRow key={op.id} op={op} onSave={save} onDelete={del} onOpenPII={setPiiOp} />)}
          </tbody>
        </table>
      </div>

      {piiOp ? (
        <SensitiveDetailsModal
          op={piiOp}
          companies={companies}
          onClose={() => setPiiOp(null)}
          onSaved={() => { setPiiOp(null); load(); }}
          onError={setErr}
        />
      ) : null}
    </div>
  );
}

function OpRow({ op, onSave, onDelete, onOpenPII }) {
  const [v, setV] = useState(op);
  useEffect(() => setV(op), [op]);
  const dirty = JSON.stringify({ ...v, cisrs_days_to_expiry: 0 }) !== JSON.stringify({ ...op, cisrs_days_to_expiry: 0 });
  const dx = op.cisrs_days_to_expiry;
  const expBadge = dx == null ? null : dx < 0 ? <span className="badge badge-red">expired</span> : dx <= 60 ? <span className="badge badge-amber"><AlertTriangle size={11} /> {dx}d</span> : <span className="badge badge-green">{dx}d</span>;
  return (
    <tr data-testid={`op-row-${op.id}`}>
      <td><input className="input-base" value={v.full_name || ""} onChange={(e) => setV({ ...v, full_name: e.target.value })} /></td>
      <td style={{ minWidth: 200 }}>
        <select data-testid={`op-type-${op.id}`} className="input-base" value={v.op_type || "Scaffolder"} onChange={(e) => setV({ ...v, op_type: e.target.value, scaffolder_grade: e.target.value === "Labourer" ? null : (v.scaffolder_grade || "Part 1") })}>
          {OP_TYPES.map((t) => <option key={t}>{t}</option>)}
        </select>
        {(v.op_type || "Scaffolder") === "Scaffolder" ? (
          <select data-testid={`op-grade-${op.id}`} className="input-base" style={{ marginTop: 4 }} value={v.scaffolder_grade || "Part 1"} onChange={(e) => setV({ ...v, scaffolder_grade: e.target.value })}>
            {SCAFFOLDER_GRADES.map((g) => <option key={g}>{g}</option>)}
          </select>
        ) : null}
      </td>
      <td><input className="input-base mono" value={v.cisrs_number || ""} onChange={(e) => setV({ ...v, cisrs_number: e.target.value })} /></td>
      <td style={{ whiteSpace: "nowrap" }}>
        <input className="input-base mono" style={{ width: 130 }} type="date" value={(v.cisrs_expiry || "").slice(0, 10)} onChange={(e) => setV({ ...v, cisrs_expiry: e.target.value })} />
        <div style={{ marginTop: 4 }}>{expBadge}</div>
      </td>
      <td><select className="input-base" value={v.pay_basis} onChange={(e) => setV({ ...v, pay_basis: e.target.value })}>{PAY_BASIS_OPTIONS.map((p) => <option key={p}>{p}</option>)}</select></td>
      <td><input className="input-base mono" style={{ textAlign: "right" }} type="number" step="0.01" value={v.hourly_rate} onChange={(e) => setV({ ...v, hourly_rate: parseFloat(e.target.value || 0) })} /></td>
      <td><input className="input-base" value={v.gang_id || ""} onChange={(e) => setV({ ...v, gang_id: e.target.value })} /></td>
      <td style={{ minWidth: 220 }}>
        <label data-testid={`op-rams-toggle-${op.id}`} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12 }}>
          <input
            data-testid={`op-rams-checkbox-${op.id}`}
            type="checkbox"
            checked={!!v.rams_signoff_authority}
            onChange={(e) => setV({ ...v, rams_signoff_authority: e.target.checked, competence_basis: e.target.checked ? (v.competence_basis || "") : "" })}
            style={{ accentColor: "#059669", width: 16, height: 16 }}
          />
          <span style={{ fontWeight: 600, color: v.rams_signoff_authority ? "#065f46" : "#475569" }}>
            {v.rams_signoff_authority ? "Authorised" : "Not authorised"}
          </span>
        </label>
        {v.rams_signoff_authority ? (
          <input
            data-testid={`op-comp-basis-${op.id}`}
            className="input-base"
            style={{ marginTop: 4, fontSize: 11 }}
            placeholder="Competence basis (e.g. SSSTS + CISRS Advanced)"
            value={v.competence_basis || ""}
            onChange={(e) => setV({ ...v, competence_basis: e.target.value })}
          />
        ) : null}
      </td>
      <td><select className="input-base" value={v.status} onChange={(e) => setV({ ...v, status: e.target.value })}><option>active</option><option>inactive</option></select></td>
      <td style={{ whiteSpace: "nowrap" }}>
        <button className="btn-primary" disabled={!dirty} style={{ padding: "4px 10px", marginRight: 4 }} onClick={() => onSave(v)} data-testid={`op-save-${op.id}`}><Save size={12} /></button>
        <button data-testid={`op-pii-${op.id}`} className="btn-ghost" style={{ padding: "4px 10px", marginRight: 4 }} title="Sensitive details (RTW · CIS · Identity · Personal)" onClick={() => onOpenPII(op)}><Shield size={12} /></button>
        <button className="btn-ghost" style={{ padding: "4px 10px" }} onClick={() => onDelete(op.id)} data-testid={`op-del-${op.id}`}><Trash2 size={12} /></button>
      </td>
    </tr>
  );
}

const ErrBox = ({ msg }) => <div className="badge badge-red" style={{ padding: "8px 12px", marginBottom: 10, width: "100%" }} data-testid="admin-err">{String(msg)}</div>;

// ---------------- Sensitive Details (RTW · CIS · Identity · Personal) ----------------
function SensitiveDetailsModal({ op, companies, onClose, onSaved, onError }) {
  const [v, setV] = useState({
    ...op,
    rtw_held: !!op.rtw_held,
    cis_verified: !!op.cis_verified,
    dbs_required: !!op.dbs_required,
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/admin/operatives/${op.id}`, v); onSaved(); }
    catch (e) { onError(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  const isCIS = v.pay_basis === "CIS-subbie";

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 80 }}>
      <div data-testid="op-pii-modal" className="card" style={{ background: "white", maxWidth: 880, width: "100%", maxHeight: "92vh", overflowY: "auto", padding: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontWeight: 900 }}><Shield size={18} style={{ verticalAlign: "middle", marginRight: 8 }} />Sensitive details · {op.full_name}</h2>
            <div className="mono" style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>Admin-only · audited · pay basis = <strong style={{ color: isCIS ? "#ff5a00" : "#0f172a" }}>{v.pay_basis}</strong></div>
          </div>
          <button data-testid="op-pii-close" className="btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>

        {/* PAY BASIS + LINK TO SUBCONTRACTOR COMPANY */}
        <Section title="Engagement">
          <Two>
            <Lbl label="Pay basis">
              <select data-testid="pii-pay-basis" className="input-base" value={v.pay_basis} onChange={(e) => setV({ ...v, pay_basis: e.target.value })}>{PAY_BASIS_OPTIONS.map((p) => <option key={p}>{p}</option>)}</select>
            </Lbl>
            <Lbl label="Supplied by company (optional)">
              <select data-testid="pii-subbie-company" className="input-base" value={v.subcontractor_company_id || ""} onChange={(e) => setV({ ...v, subcontractor_company_id: e.target.value || null })}>
                <option value="">— direct hire / self-employed —</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Lbl>
          </Two>
        </Section>

        {/* RIGHT TO WORK */}
        <Section title="Right to work (legal duty — hard gate)">
          <Two>
            <Lbl label="RTW held?">
              <label className="mono" style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 0" }}>
                <input data-testid="pii-rtw-held" type="checkbox" checked={!!v.rtw_held} onChange={(e) => setV({ ...v, rtw_held: e.target.checked })} style={{ width: 16, height: 16 }} />
                <span style={{ fontWeight: 700, color: v.rtw_held ? "#065f46" : "#b91c1c" }}>{v.rtw_held ? "HELD" : "NOT HELD"}</span>
              </label>
            </Lbl>
            <Lbl label="Check date">
              <input data-testid="pii-rtw-date" className="input-base" type="date" value={v.rtw_check_date || ""} onChange={(e) => setV({ ...v, rtw_check_date: e.target.value })} />
            </Lbl>
            <Lbl label="Method">
              <select data-testid="pii-rtw-method" className="input-base" value={v.rtw_method || ""} onChange={(e) => setV({ ...v, rtw_method: e.target.value })}>
                <option value="">—</option>
                <option value="passport">Passport</option>
                <option value="share-code">Online share code</option>
                <option value="brp">BRP</option>
                <option value="other">Other</option>
              </select>
            </Lbl>
            <Lbl label="Share code (if applicable)">
              <input data-testid="pii-rtw-share-code" className="input-base mono" value={v.rtw_share_code || ""} onChange={(e) => setV({ ...v, rtw_share_code: e.target.value })} />
            </Lbl>
            <Lbl label="RTW expiry (if time-limited)">
              <input data-testid="pii-rtw-expiry" className="input-base" type="date" value={v.rtw_expiry || ""} onChange={(e) => setV({ ...v, rtw_expiry: e.target.value })} />
            </Lbl>
            <Lbl label="RTW document URL (uploaded link)">
              <input data-testid="pii-rtw-doc" className="input-base mono" placeholder="link to scan" value={v.rtw_document_url || ""} onChange={(e) => setV({ ...v, rtw_document_url: e.target.value })} />
            </Lbl>
          </Two>
        </Section>

        {/* CIS / TAX */}
        {isCIS ? (
          <Section title="CIS / Tax (hard gate on Xero export until verified)" highlight>
            <Two>
              <Lbl label="NI number">
                <input data-testid="pii-ni" className="input-base mono" placeholder="AB123456C" value={v.ni_number || ""} onChange={(e) => setV({ ...v, ni_number: e.target.value })} />
              </Lbl>
              <Lbl label="UTR">
                <input data-testid="pii-utr" className="input-base mono" placeholder="10-digit UTR" value={v.utr || ""} onChange={(e) => setV({ ...v, utr: e.target.value })} />
              </Lbl>
              <Lbl label="CIS verified with HMRC?">
                <label className="mono" style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 0" }}>
                  <input data-testid="pii-cis-verified" type="checkbox" checked={!!v.cis_verified} onChange={(e) => setV({ ...v, cis_verified: e.target.checked, cis_verified_at: e.target.checked ? new Date().toISOString() : null })} style={{ width: 16, height: 16 }} />
                  <span style={{ fontWeight: 700, color: v.cis_verified ? "#065f46" : "#b91c1c" }}>{v.cis_verified ? "VERIFIED" : "NOT VERIFIED"}</span>
                </label>
              </Lbl>
              <Lbl label="HMRC reference">
                <input data-testid="pii-cis-ref" className="input-base mono" value={v.cis_hmrc_reference || ""} onChange={(e) => setV({ ...v, cis_hmrc_reference: e.target.value })} />
              </Lbl>
              <Lbl label="Deduction rate (%)">
                <select data-testid="pii-cis-rate" className="input-base" value={v.cis_deduction_rate ?? ""} onChange={(e) => setV({ ...v, cis_deduction_rate: e.target.value === "" ? null : Number(e.target.value) })}>
                  <option value="">—</option>
                  <option value="0">0% (gross status)</option>
                  <option value="20">20% (standard)</option>
                  <option value="30">30% (unverified)</option>
                </select>
              </Lbl>
            </Two>
          </Section>
        ) : null}

        {/* IDENTITY */}
        <Section title="Identity & address">
          <Two>
            <Lbl label="Photo ID type">
              <select data-testid="pii-photo-type" className="input-base" value={v.photo_id_type || ""} onChange={(e) => setV({ ...v, photo_id_type: e.target.value })}>
                <option value="">—</option><option>Passport</option><option>Driving licence</option>
              </select>
            </Lbl>
            <Lbl label="Photo ID document URL">
              <input data-testid="pii-photo-url" className="input-base mono" value={v.photo_id_url || ""} onChange={(e) => setV({ ...v, photo_id_url: e.target.value })} />
            </Lbl>
            <Lbl label="Address proof URL (utility bill / bank stmt)">
              <input data-testid="pii-addrproof" className="input-base mono" value={v.address_proof_url || ""} onChange={(e) => setV({ ...v, address_proof_url: e.target.value })} />
            </Lbl>
            <Lbl label="Address">
              <input data-testid="pii-address" className="input-base" value={v.address || ""} onChange={(e) => setV({ ...v, address: e.target.value })} />
            </Lbl>
          </Two>
        </Section>

        {/* PERSONAL */}
        <Section title="Personal & next of kin">
          <Two>
            <Lbl label="Date of birth">
              <input data-testid="pii-dob" className="input-base" type="date" value={v.dob || ""} onChange={(e) => setV({ ...v, dob: e.target.value })} />
            </Lbl>
            <Lbl label="Contact number">
              <input data-testid="pii-contact" className="input-base mono" value={v.contact_number || ""} onChange={(e) => setV({ ...v, contact_number: e.target.value })} />
            </Lbl>
            <Lbl label="Next of kin name">
              <input data-testid="pii-nok-name" className="input-base" value={v.next_of_kin_name || ""} onChange={(e) => setV({ ...v, next_of_kin_name: e.target.value })} />
            </Lbl>
            <Lbl label="Next of kin relationship">
              <input data-testid="pii-nok-rel" className="input-base" value={v.next_of_kin_relationship || ""} onChange={(e) => setV({ ...v, next_of_kin_relationship: e.target.value })} />
            </Lbl>
            <Lbl label="Next of kin phone">
              <input data-testid="pii-nok-phone" className="input-base mono" value={v.next_of_kin_phone || ""} onChange={(e) => setV({ ...v, next_of_kin_phone: e.target.value })} />
            </Lbl>
          </Two>
        </Section>

        {/* INDUCTIONS + DBS */}
        <Section title="Inductions & DBS">
          <Two>
            <Lbl label="Newell food-environment induction">
              <input data-testid="pii-newell-ind" className="input-base" type="date" value={v.newell_induction_date || ""} onChange={(e) => setV({ ...v, newell_induction_date: e.target.value })} />
            </Lbl>
            <Lbl label="McCain site induction">
              <input data-testid="pii-mccain-ind" className="input-base" type="date" value={v.mccain_induction_date || ""} onChange={(e) => setV({ ...v, mccain_induction_date: e.target.value })} />
            </Lbl>
            <Lbl label="DBS required?">
              <label className="mono" style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 0" }}>
                <input data-testid="pii-dbs-required" type="checkbox" checked={!!v.dbs_required} onChange={(e) => setV({ ...v, dbs_required: e.target.checked })} style={{ width: 16, height: 16 }} />
                <span style={{ fontWeight: 700 }}>{v.dbs_required ? "REQUIRED" : "—"}</span>
              </label>
            </Lbl>
            {v.dbs_required ? (
              <>
                <Lbl label="DBS number">
                  <input data-testid="pii-dbs-no" className="input-base mono" value={v.dbs_number || ""} onChange={(e) => setV({ ...v, dbs_number: e.target.value })} />
                </Lbl>
                <Lbl label="DBS expiry">
                  <input data-testid="pii-dbs-expiry" className="input-base" type="date" value={v.dbs_expiry || ""} onChange={(e) => setV({ ...v, dbs_expiry: e.target.value })} />
                </Lbl>
              </>
            ) : null}
          </Two>
        </Section>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16, borderTop: "1px solid #e2e8f0", paddingTop: 16 }}>
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button data-testid="op-pii-save" className="btn-primary" onClick={save} disabled={busy}><Save size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />{busy ? "Saving…" : "Save sensitive details"}</button>
        </div>
      </div>
    </div>
  );
}

const Section = ({ title, children, highlight }) => (
  <fieldset style={{ border: `1px solid ${highlight ? "#fde68a" : "#e2e8f0"}`, background: highlight ? "#fffbeb" : "transparent", borderRadius: 6, padding: 12, marginTop: 12 }}>
    <legend className="mono" style={{ fontSize: 11, letterSpacing: "0.08em", color: highlight ? "#92400e" : "#475569", fontWeight: 700, padding: "0 8px" }}>{title}</legend>
    {children}
  </fieldset>
);

const Two = ({ children }) => <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>{children}</div>;

const Lbl = ({ label, children }) => (
  <label style={{ display: "block" }}>
    <div className="mono" style={{ fontSize: 10, color: "#64748b", marginBottom: 2 }}>{label}</div>
    {children}
  </label>
);

// ---------------- Branding ----------------
function Branding() {
  const branding = useBranding();
  const [draft, setDraft] = useState({ primary_dark: "", accent_orange: "", heading_bg: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  useEffect(() => {
    setDraft({
      primary_dark: branding.primary_dark || "#1a1a1a",
      accent_orange: branding.accent_orange || "#F05A14",
      heading_bg: branding.heading_bg || "#f1f5f9",
    });
  }, [branding.primary_dark, branding.accent_orange, branding.heading_bg]);

  const saveColours = async () => {
    setBusy(true); setErr(""); setOk("");
    try {
      await api.put("/admin/branding", draft);
      await branding.reload?.();
      setOk("Colours saved — applied across the app and PDF documents.");
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const uploadFile = async (which, file) => {
    setErr(""); setOk("");
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/branding/${which}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await branding.reload?.();
      setOk(`${which === "logo" ? "Logo" : "Accreditations"} updated.`);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
  };

  const resetImage = async (which) => {
    if (!window.confirm(`Reset ${which} to the packaged default?`)) return;
    setErr(""); setOk("");
    try {
      await api.delete(`/admin/branding/${which}`);
      await branding.reload?.();
      setOk(`${which === "logo" ? "Logo" : "Accreditations"} reset to default.`);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
  };

  const Swatch = ({ label, field, testid }) => (
    <div>
      <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#475569" }}>{label}</label>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
        <input data-testid={`${testid}-picker`} type="color" value={draft[field]} onChange={(e) => setDraft({ ...draft, [field]: e.target.value })} style={{ width: 56, height: 44, border: "1px solid #cbd5e1", padding: 2, cursor: "pointer" }} />
        <input data-testid={`${testid}-hex`} className="input-base mono" value={draft[field]} onChange={(e) => setDraft({ ...draft, [field]: e.target.value })} placeholder="#1a1a1a" style={{ maxWidth: 140 }} />
      </div>
    </div>
  );

  return (
    <div data-testid="branding-panel">
      {err ? <ErrBox msg={err} /> : null}
      {ok ? <div className="badge badge-green" style={{ padding: "8px 12px", marginBottom: 10, width: "100%" }} data-testid="branding-ok">{ok}</div> : null}

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>Brand colours</div>
        <div className="mono" style={{ fontSize: 11, color: "#94a3b8", marginBottom: 12 }}>
          Applied live to the app (sidebar background, accents, section headings) and to both PDF documents (header bar, reference accent, category strip background).
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 12 }}>
          <Swatch label="Primary / dark (heading background)" field="primary_dark" testid="brand-primary" />
          <Swatch label="Accent / orange" field="accent_orange" testid="brand-accent" />
          <Swatch label="Section heading background (PDF)" field="heading_bg" testid="brand-heading-bg" />
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <button data-testid="brand-save-colours" className="btn-primary" disabled={busy} onClick={saveColours}>
            <Save size={14} style={{ verticalAlign: -2, marginRight: 6 }} /> Save colours
          </button>
          <div className="card" style={{ padding: "6px 12px", background: draft.primary_dark, color: "white", borderRadius: 2, fontSize: 12, fontWeight: 700 }}>HEADER PREVIEW</div>
          <div className="card" style={{ padding: "6px 12px", background: draft.accent_orange, color: "white", borderRadius: 2, fontSize: 12, fontWeight: 700 }}>ACCENT</div>
          <div className="card" style={{ padding: "6px 12px", background: draft.heading_bg, color: "#0f172a", borderRadius: 2, fontSize: 12, fontWeight: 700 }}>SECTION HEADING</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div className="card" style={{ padding: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Logo</div>
          <div className="mono" style={{ fontSize: 11, color: "#94a3b8", marginBottom: 12 }}>
            PNG recommended (transparent background). Appears on the login page, the sidebar header on every page, and the top-left of both PDF documents. Max 5 MB.
          </div>
          {branding.logo_data_url ? (
            <div style={{ background: "#1a1a1a", padding: 16, marginBottom: 12 }}>
              <img src={branding.logo_data_url} alt="Current logo" style={{ maxHeight: 90, display: "block" }} data-testid="branding-logo-preview" />
            </div>
          ) : null}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <label className="btn-primary" style={{ cursor: "pointer" }}>
              <Upload size={14} style={{ verticalAlign: -2, marginRight: 6 }} /> Upload new logo
              <input data-testid="branding-logo-input" type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => uploadFile("logo", e.target.files?.[0])} />
            </label>
            <button className="btn-ghost" onClick={() => resetImage("logo")} data-testid="branding-logo-reset">
              <RefreshCw size={14} style={{ verticalAlign: -2, marginRight: 6 }} /> Reset to packaged default
            </button>
          </div>
        </div>

        <div className="card" style={{ padding: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Accreditations strip</div>
          <div className="mono" style={{ fontSize: 11, color: "#94a3b8", marginBottom: 12 }}>
            Horizontal strip of certification logos. Appears in the footer of both PDF documents only. Wide aspect ratio (≈10:1) works best. Max 5 MB. <b>Only upload accreditations Newell actually holds.</b>
          </div>
          {branding.accreditations_data_url ? (
            <div style={{ background: "#000", padding: 8, marginBottom: 12 }}>
              <img src={branding.accreditations_data_url} alt="Accreditations" style={{ width: "100%", display: "block" }} data-testid="branding-accred-preview" />
            </div>
          ) : null}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <label className="btn-primary" style={{ cursor: "pointer" }}>
              <Upload size={14} style={{ verticalAlign: -2, marginRight: 6 }} /> Upload new strip
              <input data-testid="branding-accred-input" type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => uploadFile("accreditations", e.target.files?.[0])} />
            </label>
            <button className="btn-ghost" onClick={() => resetImage("accreditations")} data-testid="branding-accred-reset">
              <RefreshCw size={14} style={{ verticalAlign: -2, marginRight: 6 }} /> Reset to packaged default
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function McCainContacts() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", role_title: "", active: true });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/mccain-contacts"); setRows(data); }
    catch (e) { setError(e.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/mccain-contacts", form);
      setForm({ name: "", email: "", role_title: "", active: true });
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : JSON.stringify(d));
    } finally { setBusy(false); }
  };

  const toggle = async (c) => {
    try { await api.patch(`/mccain-contacts/${c.id}`, { active: !c.active }); load(); }
    catch (e) { setError(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this McCain contact?")) return;
    try { await api.delete(`/mccain-contacts/${id}`); load(); }
    catch (e) { setError(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="mccain-contacts-tab">
      <div className="card" style={{ padding: 16, marginBottom: 16, background: "#fff7ed", borderLeft: "4px solid #ff5a00" }}>
        <div className="mono" style={{ fontSize: 11, color: "#9a3412", letterSpacing: "0.1em", marginBottom: 4 }}>NRS CONFIRMATION ROUTING</div>
        <div style={{ fontSize: 13, color: "#0f172a" }}>When a responder acknowledges a callout with their ETA, the system emails these contacts: <strong>&quot;X is responding, on site in ~Y minutes&quot;</strong>. Only active contacts receive the notification.</div>
      </div>

      <form onSubmit={add} className="card" style={{ padding: 16, marginBottom: 16, display: "grid", gridTemplateColumns: "1.2fr 1.4fr 1fr auto", gap: 10, alignItems: "end" }}>
        <label>
          <div className="mono" style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>NAME *</div>
          <input data-testid="mc-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </label>
        <label>
          <div className="mono" style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>EMAIL *</div>
          <input data-testid="mc-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </label>
        <label>
          <div className="mono" style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>ROLE / TITLE</div>
          <input data-testid="mc-role" value={form.role_title} onChange={(e) => setForm({ ...form, role_title: e.target.value })} />
        </label>
        <button data-testid="mc-add" className="btn-primary" disabled={busy}><Plus size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />Add</button>
      </form>

      {error ? <div data-testid="mc-error" className="card" style={{ padding: 10, marginBottom: 12, background: "#fef2f2", color: "#b91c1c", borderLeft: "4px solid #dc2626", fontSize: 12 }}><AlertTriangle size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />{error}</div> : null}

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table className="responsive-table" data-testid="mc-table">
          <thead><tr><th>Name</th><th>Email</th><th>Role / Title</th><th>Status</th><th style={{ textAlign: "right" }}>Actions</th></tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={5} style={{ padding: 24, textAlign: "center", color: "#64748b" }}>No McCain contacts configured yet. Add one above so NRS confirmations have somewhere to land.</td></tr>
              : rows.map((c) => (
                <tr key={c.id} data-testid={`mc-row-${c.id}`}>
                  <td data-label="Name" style={{ fontWeight: 600 }}>{c.name}</td>
                  <td data-label="Email" className="mono" style={{ fontSize: 12 }}>{c.email}</td>
                  <td data-label="Role">{c.role_title || <span style={{ color: "#94a3b8" }}>—</span>}</td>
                  <td data-label="Status"><span className="mono" style={{ background: c.active ? "#dcfce7" : "#f1f5f9", color: c.active ? "#065f46" : "#64748b", padding: "2px 8px", borderRadius: 3, fontSize: 10, fontWeight: 700, letterSpacing: "0.08em" }}>{c.active ? "ACTIVE" : "MUTED"}</span></td>
                  <td data-label="Actions" style={{ textAlign: "right" }}>
                    <button data-testid={`mc-toggle-${c.id}`} className="btn-ghost" onClick={() => toggle(c)}>{c.active ? "Mute" : "Activate"}</button>
                    <button data-testid={`mc-delete-${c.id}`} className="btn-ghost" style={{ color: "#b91c1c" }} onClick={() => remove(c.id)}><Trash2 size={12} /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
